import React, { useState, useEffect, useRef } from 'react';
import MaterialTable from "material-table";
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { api } from 'common';
import { useTranslation } from "react-i18next";
import { colors } from '../components/Theme/WebTheme';
import moment from 'moment/min/moment-with-locales';
import { useNavigate } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';

export default function DisputesList() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loaded = useRef(false);

  const { fetchDisputes, fetchBookings } = api;
  const disputedata = useSelector(state => state.disputedata.disputes);
  const bookinglistdata = useSelector(state => state.bookinglistdata);
  const settings = useSelector(state => state.settingsdata?.settings);

  useEffect(() => {
    dispatch(fetchDisputes(null, 'admin'));
    if (!bookinglistdata?.bookings) {
      dispatch(fetchBookings());
    }
  }, [dispatch, fetchDisputes, fetchBookings, bookinglistdata?.bookings]);

  const [data, setData] = useState([]);
  useEffect(() => {
    if (disputedata) {
      const bookings = bookinglistdata?.bookings || [];
      const bookingMap = new Map();
      bookings.forEach(b => {
        if (b.id) bookingMap.set(b.id, b);
      });

      const formatted = disputedata.map(c => {
        const b = bookingMap.get(c.booking_id);
        const refundAmt = (c.refund_amount !== undefined && c.refund_amount !== null && c.refund_amount !== '')
          ? c.refund_amount
          : (b && b.refund_amount !== undefined && b.refund_amount !== null && b.refund_amount !== '' ? b.refund_amount : null);
        
        const refundDest = c.refund_destination || c.refund_method || (b ? (b.refund_destination || b.refund_method) : null);
        const refundStatus = c.refund_status || (b && b.refund_status ? (b.refund_status === 'refunded' ? 'Refunded' : b.refund_status) : null);

        return {
          ...c,
          refund_amount: refundAmt,
          refund_destination: refundDest,
          refund_status: refundStatus
        };
      });

      setData(formatted);
    } else {
      setData([]);
    }
    loaded.current = true;
  }, [disputedata, bookinglistdata]);

  const columns = [
    { title: t('booking_id', 'Booking ID'), field: 'booking_id', editable: 'never' },
    { title: t('date', 'Date'), field: 'createdAt', editable: 'never', defaultSort: 'desc', render: rowData => rowData.createdAt ? moment(rowData.createdAt).format('lll') : null },
    { title: t('ticket_status', 'Ticket Status'), field: 'status', editable: 'never' },
    { title: t('passenger_id', 'Passenger ID'), field: 'passenger_id', editable: 'never' },
    { title: t('refund_status', 'Refund Status'), field: 'refund_status', editable: 'never' },
    { 
      title: t('refund_amount', 'Refund Amount'), 
      field: 'refund_amount', 
      editable: 'never',
      render: rowData => {
        if (rowData.refund_amount === undefined || rowData.refund_amount === null || rowData.refund_amount === '') return '-';
        return settings?.symbol ? `${settings.symbol}${parseFloat(rowData.refund_amount).toFixed(settings?.decimal || 2)}` : rowData.refund_amount;
      }
    },
    { 
      title: t('refund_destination', 'Refund Destination'), 
      field: 'refund_destination', 
      editable: 'never',
      render: rowData => {
        const dest = rowData.refund_destination || rowData.refund_method;
        if (!dest) return '-';
        return dest === 'original' ? t('original_payment_stripe', 'Original Payment (Stripe)') : t('passenger_wallet', 'Passenger Wallet');
      }
    },
  ];

  const [selectedRow, setSelectedRow] = useState(null);

  return (
    !loaded.current ? <CircularLoading /> :
      <MaterialTable
        title={t('disputes_desk', 'Disputes Desk')}
        style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr', borderRadius: "8px", boxShadow: "4px 4px 6px #9E9E9E" }}
        data={data}
        columns={columns}
        onRowClick={((evt, selectedRow) => setSelectedRow(selectedRow.tableData.id))}
        options={{
          exportButton: true,
          rowStyle: rowData => ({
            backgroundColor: (selectedRow === rowData.tableData.id) ? colors.THIRDCOLOR : colors.WHITE
          }),
          headerStyle: {
            backgroundColor: colors.SECONDARY,
            color: colors.WHITE,
            fontWeight: 'bold',
          },
          actionsColumnIndex: -1
        }}
        actions={[
          {
            icon: () => <VisibilityIcon />,
            tooltip: t('view_and_refund', 'View & Refund'),
            onClick: (event, rowData) => {
              navigate(`/dispute-details/${rowData.id}`);
            }
          }
        ]}
      />
  );
}
